// predictive-search.js — full-screen search overlay with a live product feed.
// Renders products (image, title, price) + query suggestions live from
// /search/suggest.json as the shopper types. Falls back gracefully (no images,
// no GSAP). Default state shows recent (localStorage) + popular searches.

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const RECENT_KEY = 'taabaa-recent-search';

/* Shopify-standard money formatter (handles the merchant's money_format). */
function makeMoney(format) {
  function withDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    number = (number / 100).toFixed(precision);
    const parts = number.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const frac = parts[1] ? decimal + parts[1] : '';
    return whole + frac;
  }
  return function (amount) {
    const cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents)) return '';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const m = (format || '${{amount}}').match(re);
    let val = '';
    switch (m ? m[1] : 'amount') {
      case 'amount': val = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': val = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': val = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': val = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': val = withDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': val = withDelimiters(cents, 2, "'"); break;
      default: val = withDelimiters(cents, 2);
    }
    return (format || '${{amount}}').replace(re, val);
  };
}

// Never offered on their own, so never suggested: builder parts (wrapping
// paper, hamper chocolates), the Rs 0 free-gift twins and hidden colour
// duplicates. /search/suggest.json returns tags and type, so filter on both,
// the same rule the collection and search grids use.
const HIDDEN_TAGS = ['component', 'free-gift', 'colour-duplicate'];
const HIDDEN_TYPES = ['wrapping paper', 'free gift', 'chocolate'];
function isHidden(p) {
  const tags = (p.tags || []).map((t) => String(t).toLowerCase());
  if (HIDDEN_TAGS.some((t) => tags.includes(t))) return true;
  return HIDDEN_TYPES.includes(String(p.type || '').toLowerCase());
}
const SHOW = 8;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const PLACEHOLDER = '<span class="absolute inset-0 flex items-center justify-center text-foreground/20"><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.6"/><path d="M21 15l-5-5L5 21"/></svg></span>';

class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultPane = this.querySelector('[data-search-default]');
    this.panel = this.querySelector('[data-search-panel]');
    this.overlay = this.querySelector('[data-search-overlay]');
    this.form = this.querySelector('form');
    this.money = makeMoney(this.getAttribute('data-money-format'));
    this.searchUrl = this.getAttribute('data-search-url') || '/search';
    this.rootUrl = this.getAttribute('data-root-url') || '/';
    this.defaultHTML = this.defaultPane ? this.defaultPane.outerHTML : '';

    document.addEventListener('search:open', () => this.open());
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.input?.addEventListener('input', this.debounce(() => this.query(), 220));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.hidden) this.close(); });
    // Keep Tab inside the open overlay (it wraps from last to first).
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || this.hidden) return;
      const scope = this.panel || this;
      const f = Array.from(scope.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !scope.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Persist the term when a shopper acts on a result.
    this.results?.addEventListener('click', (e) => { if (e.target.closest('a')) this.remember(this.input.value); });
    this.form?.addEventListener('submit', () => this.remember(this.input.value));

    this.renderRecent();
  }

  open() {
    // Remember what opened the overlay so focus can go back there on close.
    if (this.hidden) this.opener = document.activeElement;
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this.renderRecent();
    const g = window.gsap;
    if (g && document.documentElement.classList.contains('force-motion')) {
      g.fromTo(this.overlay, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
      g.fromTo(this.panel, { y: -24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out' });
    }
    setTimeout(() => this.input?.focus(), 60);
  }

  close() {
    const back = this.opener;
    this.opener = null;
    const done = () => {
      this.hidden = true;
      document.body.style.overflow = '';
      if (back && back.focus && document.contains(back)) { try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); } }
    };
    const g = window.gsap;
    if (g && document.documentElement.classList.contains('force-motion') && !this.hidden) {
      g.to(this.panel, { y: -16, opacity: 0, duration: 0.25, ease: 'power2.in' });
      g.to(this.overlay, { opacity: 0, duration: 0.25, ease: 'power2.in', onComplete: done });
    } else { done(); }
  }

  debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }

  remember(term) {
    term = String(term || '').trim();
    if (term.length < 2) return;
    try {
      let list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      list = [term].concat(list.filter((x) => x.toLowerCase() !== term.toLowerCase())).slice(0, 6);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  renderRecent() {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) {}
    const wrap = this.querySelector('[data-search-recent]');
    const target = this.querySelector('[data-search-recent-list]');
    if (!wrap || !target) return;
    if (!list.length) { wrap.classList.add('hidden'); return; }
    wrap.classList.remove('hidden');
    target.innerHTML = list.map((t) =>
      `<a href="${esc(this.searchUrl)}?q=${encodeURIComponent(t)}&type=product" class="inline-flex items-center rounded-full border-2 border-foreground/25 px-4 py-2 font-serif uppercase text-base text-foreground hover:bg-foreground hover:text-background transition-colors">${esc(t)}</a>`
    ).join('');
  }

  showDefault() {
    if (this.defaultHTML) this.results.innerHTML = this.defaultHTML;
    this.renderRecent();
  }

  async query() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.showDefault(); return; }
    this.results.innerHTML = '<p class="py-10 text-center text-foreground/45 font-serif uppercase text-lg">Searching…</p>';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.rootUrl;
    const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=10&resources[options][unavailable_products]=last`;
    let data;
    try { data = await (await fetch(url)).json(); }
    catch (e) { this.results.innerHTML = '<p class="py-10 text-center text-foreground/45 font-serif uppercase text-lg">Something went wrong</p>'; return; }
    if (this.input.value.trim() !== q) return; // a newer keystroke already fired
    this.render(q, data);
  }

  render(q, data) {
    const r = (data && data.resources && data.resources.results) || {};
    const products = (r.products || []).filter((p) => !isHidden(p)).slice(0, SHOW);
    const queries = (r.queries || []).filter((x) => (x.text || '').toLowerCase() !== q.toLowerCase()).slice(0, 5);
    const viewAll = `${this.searchUrl}?q=${encodeURIComponent(q)}&type=product`;

    if (!products.length && !queries.length) {
      this.results.innerHTML = `<p class="py-10 text-center text-foreground/55 font-serif uppercase text-lg">No results for “${esc(q)}”</p>
        <div class="text-center"><a href="${esc(viewAll)}" class="inline-flex items-center gap-2 font-serif uppercase text-base text-foreground underline underline-offset-4">Search all products</a></div>`;
      return;
    }

    let html = '';
    if (queries.length) {
      html += '<div class="flex flex-wrap gap-2 mb-5">' + queries.map((s) =>
        `<a href="${esc(this.searchUrl)}?q=${encodeURIComponent(s.text)}&type=product" class="inline-flex items-center gap-1.5 rounded-full border-2 border-foreground/20 px-3.5 py-1.5 font-sans text-small text-foreground hover:bg-foreground hover:text-background transition-colors">${esc(s.text)}</a>`
      ).join('') + '</div>';
    }

    if (products.length) {
      html += '<p class="font-sans text-small uppercase tracking-[0.16em] text-foreground/45 mb-3">Products</p>';
      html += '<div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">' + products.map((p) => {
        const img = (p.featured_image && p.featured_image.url) || p.image || '';
        const sep = String(img).indexOf('?') > -1 ? '&' : '?';
        // contain, not cover: a photo is never cropped (most are 4:5 portraits)
        const media = img
          ? `<img src="${esc(img + sep)}width=400" alt="" loading="lazy" width="300" height="300" class="absolute inset-0 w-full h-full object-contain">`
          : PLACEHOLDER;
        // Price honestly. A product whose sizes differ in price reads "From"
        // the cheapest, struck through only against the lowest compare-at
        // (price_max vs compare_at_price_max paired the Rs 1,290 Mini hamper
        // with the Rs 6,490 compare-at of the Grand one). A builder that costs
        // nothing until filled (price_max 0) is not "Rs 0".
        const pMin = parseFloat(p.price_min != null ? p.price_min : p.price) || 0;
        const pMax = parseFloat(p.price_max != null ? p.price_max : p.price) || 0;
        const varies = pMax > pMin;
        const cmp = parseFloat(varies ? p.compare_at_price_min : (p.compare_at_price_max || p.compare_at_price_min)) || 0;
        const onSale = cmp > pMin;
        const price = pMax === 0
          ? '<span class="font-semibold text-foreground">Build your own</span>'
          : `<span class="font-semibold ${onSale ? 'text-orange' : 'text-foreground'}">${varies ? 'From ' : ''}${esc(this.money(pMin))}</span>${onSale ? `<span class="line-through text-foreground/40">${esc(this.money(cmp))}</span>` : ''}`;
        return `<a href="${esc(p.url)}" class="group/sr block">
          <span class="relative block rounded-xl overflow-hidden aspect-square mb-2" style="background:#efe3cd;">${media}</span>
          <span class="block font-serif uppercase text-base leading-tight text-foreground line-clamp-2">${esc(p.title)}</span>
          <span class="mt-1 flex items-center gap-2 text-small">${price}</span>
        </a>`;
      }).join('') + '</div>';
      html += `<div class="mt-5"><a href="${esc(viewAll)}" class="inline-flex items-center gap-2 font-serif uppercase text-lg text-foreground underline underline-offset-4">View all results for “${esc(q)}”</a></div>`;
    }

    this.results.innerHTML = html;
  }
}
customElements.define('search-modal', SearchModal);
